import { useState, useEffect, useRef } from 'react'
import { SupportButton } from '@/components/global/Support/SupportButton'
import {
  Button,
  CaseDetails,
  FullscreenModal,
  OrderDashboardButton,
} from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { caseAtom, hasNewCaseAtom, applicationTypeAtom } from 'atoms'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useCaseImageVariants } from '@/hooks/caseImages'
import { EventCategory, logger } from 'utils/logger'
import {
  isDesignerTokenSession,
  isQcTokenSession,
} from 'utils/global/designerSession'
import { useRecoilValue } from 'recoil'
import { useTranslation } from 'react-i18next'
import LanguageSelector from 'components/global/LanguageSelector/LanguageSelector'
import { M3 } from '@messenger/consts'
import { PRINT_PACKAGE_HEADER_TEST_IDS } from './printPackageHeaderTestIds'

export const PrintPackageHeader = ({ title, caseInfo, ...props }) => {
  const { t } = useTranslation()
  const [showCaseDetailsModal, setShowCaseDetailsModal] = useState(false)
  const _case = useRecoilValue(caseAtom)
  const hasNewCase = useRecoilValue(hasNewCaseAtom)
  const isAdmin = useIsAdmin()
  const applicationType = useRecoilValue(applicationTypeAtom)
  const { loadCaseImages } = useCaseImageVariants()
  // True from an Edit Case click until its modal opens. The ref answers a
  // second click in the same tick, before the disabled control has drawn.
  const [openingCaseDetails, setOpeningCaseDetails] = useState(false)
  const openingCaseDetailsRef = useRef(false)

  useEffect(() => {
    if (hasNewCase) {
      setShowCaseDetailsModal(true)
    }
  }, [hasNewCase])

  const openCaseDetailsModal = async () => {
    if (!_case?.id || openingCaseDetailsRef.current) return
    openingCaseDetailsRef.current = true
    setOpeningCaseDetails(true)
    try {
      // The modal opens once the image list has answered, the same as when it
      // opens from the dashboard; the list request has its own timeout, and a
      // failed list still opens the modal, with no images rather than those
      // of a case opened before (clearOnFailure, as the dashboard opens it).
      // The case's AI variants and, unless AI image edits are known to be
      // off, the variant pricing are requested alongside and land when they
      // answer.
      await loadCaseImages(_case.id, { clearOnFailure: true })
    } catch (err) {
      // The logger keeps only the error's name, message and stack, never the
      // request config, whose headers carry the session's bearer token.
      logger.warn({
        message: 'case_images_load_failed',
        category: EventCategory.ERROR,
        error: err instanceof Error ? err : undefined,
        data: { caseId: _case.id, httpStatus: err?.response?.status },
      })
    } finally {
      openingCaseDetailsRef.current = false
      setOpeningCaseDetails(false)
    }
    setShowCaseDetailsModal(true)
  }

  return (
    <Flex
      width="100%"
      padding="24px"
      justify="space-between"
      align="center"
      background="lockedAlmostBlack"
      borderBottom="1px solid"
      borderColor="gray4"
      borderOpacity="25"
      {...props}
    >
      <Flex align="center" gap="24px">
        <Text size="32px" weight="bold" color="lockedWhite">
          {title}
        </Text>
        <SupportButton />
        <OrderDashboardButton />
      </Flex>
      <Flex align="center">
        <Flex column align="flex-end" gap="2px">
          <Text size="14px" margin="0 16px 0 0" color="lockedWhite">
            {caseInfo}
          </Text>
          <Text color="gray" margin="0 16px 0 0" justify="flex-end" size="12px">
            {t('package.accessLabel', {
              role: t(
                isDesignerTokenSession()
                  ? 'package.roleDesigner'
                  : isQcTokenSession()
                    ? 'package.roleQc'
                    : isAdmin
                      ? 'package.roleSuperAdmin'
                      : 'package.roleFuneralHome',
              ),
            })}
          </Text>
        </Flex>
        <Button
          primary
          disabled={!_case?.id || openingCaseDetails}
          aria-busy={openingCaseDetails}
          data-testid={PRINT_PACKAGE_HEADER_TEST_IDS.EDIT_CASE}
          onClick={openCaseDetailsModal}
        >
          {openingCaseDetails
            ? t('package.editCaseLoading')
            : t('package.editCaseButton')}
        </Button>
        {!isAdmin && applicationType !== M3 && <LanguageSelector whiteIcon />}
      </Flex>

      {showCaseDetailsModal && (
        <FullscreenModal
          show={[showCaseDetailsModal, setShowCaseDetailsModal]}
          data-testid={PRINT_PACKAGE_HEADER_TEST_IDS.CASE_DETAILS_MODAL}
        >
          <CaseDetails />
        </FullscreenModal>
      )}
    </Flex>
  )
}

export default PrintPackageHeader
